<template>
  <div>
    <section class="section is-main-section">
      <CardComponent>
        <div class="section has-text-centered">
          <h1 class="title">
            Manage Episodes for {{ mediaItem && mediaItem.title }}
            <span v-if="mediaItem && mediaItem.mediadata && mediaItem.mediadata.seriesNo" class="subtitle is-5 has-text-grey">
              (Season {{ mediaItem.mediadata.seriesNo }})
            </span>
          </h1>
          <div class="buttons-list">
            <b-button
              type="is-light"
              icon-left="arrow-left"
              @click="backToTitle"
            >
              Back to Title
            </b-button>
          </div>
          <div class="buttons-list has-text-right has-margin-top-10">
            <b-button
              type="is-primary"
              icon-left="plus"
              tag="router-link"
              :to="{ name: 'CreateEpisode' }"
              >Add</b-button
            >
            <b-button
              type="is-default"
              class="has-margin-left-5"
              @click="toggleSelectAll"
            >
              {{ allFilteredSelected ? 'Clear Selection' : 'Select All' }}
              <span v-if="selectedCount > 0">({{ selectedCount }})</span>
            </b-button>
            <b-button
              v-if="anySelected"
              type="is-primary"
              :disabled="loading"
              class="has-margin-left-5"
              :class="{ 'is-loading': loading }"
              @click="deleteEpisodes"
            >
              Delete</b-button
            >
            <b-button
              type="is-default"
              class="has-margin-left-5"
              @click="autoGenerate"
              >Auto Generate</b-button
            >
            <b-button
              type="is-warning"
              class="has-margin-left-5"
              icon-left="refresh"
              :loading="regeneratingFilenames"
              @click="regenerateTxFilenames"
              >Regenerate TX Filenames</b-button
            >
            <b-button
              type="is-default"
              class="has-margin-left-5"
              @click="copyAll"
              >Copy All File Names</b-button
            >
            <b-button
              type="is-info"
              icon-left="download"
              class="has-margin-left-5"
              @click="exportSegmentsCSV"
              >Export Segments CSV</b-button
            >
          </div>
          <b-field class="has-margin-top-15">
            <b-input
              v-model="search"
              placeholder="Search..."
              type="search"
              icon="magnify"
            >
            </b-input>
          </b-field>
          <b-table
            :data="filteredEpisodes"
            :bordered="false"
            :striped="true"
            :narrowed="true"
            :hoverable="true"
            :loading="loading"
            :focusable="false"
            :mobile-cards="true"
            :paginated="true"
            detailed
            :opened-detailed="openedDetails"
            detail-key="id"
            @details-open="onDetailsOpen"
          >
            <template slot-scope="props">
              <b-table-column field="" label="" width="40">
                <b-checkbox v-model="props.row.selected" />
              </b-table-column>

              <b-table-column field="episode_number" label="#" numeric width="60">
                {{ props.row.episode_number }}
              </b-table-column>

              <b-table-column field="title" label="Title" width="200">
                {{ props.row.title }}
              </b-table-column>

              <b-table-column label="Segments" width="80">
                <b-tag type="is-info" size="is-small">
                  {{ segmentsByEpisode[props.row.id] ? segmentsByEpisode[props.row.id].length : '...' }}
                </b-tag>
              </b-table-column>

              <b-table-column label="Duration" width="80">
                <b-input
                  v-model="props.row.episodeLength"
                  type="number"
                  size="is-small"
                  rules="required"
                  :disabled="hasSegments(props.row.id)"
                  @input="updateRow(props.row)"
                />
                <p v-if="hasSegments(props.row.id)" class="is-size-7 has-text-grey">
                  <router-link :to="{ name: 'EditEpisode', params: { id: props.row.id } }">
                    Edit via segments
                  </router-link>
                </p>
              </b-table-column>

              <b-table-column label="PG" width="60">
                <b-input
                  v-model="props.row.ageRestriction"
                  size="is-small"
                  placeholder="-"
                  @blur="updateRow(props.row)"
                />
              </b-table-column>

              <b-table-column label="Synopsis">
                <b-input
                  v-model="props.row.description"
                  type="textarea"
                  size="is-small"
                  rows="2"
                  maxlength="180"
                  placeholder="Episode synopsis..."
                  @blur="updateRow(props.row)"
                />
              </b-table-column>

              <b-table-column field="file_status" label="TX File Status" width="130">
                <b-tag :type="getTxFileStatusType(props.row)" size="is-small">
                  {{ getTxFileStatusLabel(props.row) }}
                </b-tag>
                <div v-if="jobProgress[props.row.id]" class="job-progress mt-1">
                  <b-progress
                    :value="jobProgress[props.row.id].progress_percent || 0"
                    :max="100"
                    type="is-info"
                    size="is-small"
                  />
                  <span class="is-size-7 has-text-info">
                    {{ jobProgress[props.row.id].phase || jobProgress[props.row.id].status
                    }}<template v-if="jobProgress[props.row.id].progress_percent != null"> · {{ Math.round(jobProgress[props.row.id].progress_percent) }}%</template>
                  </span>
                </div>
                <a
                  v-if="props.row.stitch_qc_status === 'failed'"
                  href=""
                  class="is-size-7 has-text-danger"
                  @click.prevent="showQcJobResult(props.row)"
                ><br>View QC Errors</a>
              </b-table-column>
              <!-- <b-table-column field="manualqcstatus" label="Manual QC">
                <a href="" @click.prevent="changeManualStatus(props.row)">{{
                  (props.row.manualqcstatus || 'Not Available') | capitalize
                }}</a>
              </b-table-column> -->
              <b-table-column v-if="mediaItem" label="TX Filename" width="180">
                <p class="filename-text">
                  <span class="has-text-weight-bold has-text-info">S{{ mediaItem.mediadata && mediaItem.mediadata.seriesNo ? String(mediaItem.mediadata.seriesNo).padStart(2, '0') : '00' }}</span>
                  {{ props.row.fileName }}
                </p>
                <a href="" class="is-size-7" @click.prevent="copyText(props.row.fileName)"
                  >COPY</a
                >
              </b-table-column>

              <b-table-column label="Actions" width="120">
                <b-button
                  size="is-small"
                  class="has-margin-right-5"
                  @click="
                    $router.push({
                      name: 'EditEpisode',
                      params: { id: props.row.id }
                    })
                  "
                  >Edit</b-button
                >
                <b-button
                  size="is-small"
                  @click="confirmDelete(props.row)"
                  >Delete</b-button
                >
              </b-table-column>

            </template>

            <!-- Expanded Detail Row - Full Width Segment Editor -->
            <template slot="detail" slot-scope="props">
              <div class="segment-details-container">
                <div v-if="!segmentsByEpisode[props.row.id]" class="has-text-centered" style="padding: 20px;">
                  <b-icon icon="loading" custom-class="mdi-spin" size="is-medium"></b-icon>
                  <p>Loading segments...</p>
                </div>
                <div v-else-if="segmentsByEpisode[props.row.id].length === 0" class="has-text-centered has-text-grey" style="padding: 20px;">
                  <b-icon icon="alert-circle-outline" size="is-medium"></b-icon>
                  <p>No segments defined for this episode.</p>
                  <router-link :to="{ name: 'EditEpisode', params: { id: props.row.id } }" class="button is-small is-primary has-margin-top-10">
                    Add Segments
                  </router-link>
                </div>
                <div v-else>
                  <h4 class="subtitle is-6 has-margin-bottom-10">
                    <b-icon icon="movie-edit" size="is-small"></b-icon>
                    Segment Timecodes for Episode {{ props.row.episode_number }}: {{ props.row.title }}
                  </h4>
                  <table class="table is-narrow is-fullwidth is-bordered segment-table">
                    <thead>
                      <tr>
                        <th width="80">Segment</th>
                        <th width="100">Type</th>
                        <th width="180">In Point (HH:MM:SS:FF)</th>
                        <th width="180">Out Point (HH:MM:SS:FF)</th>
                        <th width="140">Duration</th>
                        <th>Notes</th>
                        <th width="100">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="segment in segmentsByEpisode[props.row.id]" :key="segment.id">
                        <td class="has-text-centered has-text-weight-bold">
                          {{ segment.segment_number }}
                        </td>
                        <td>
                          <b-tag :type="segment.segment_type === 'content' ? 'is-info' : 'is-warning'">
                            {{ segment.segment_type }}
                          </b-tag>
                        </td>
                        <td>
                          <b-input
                            v-model="segment.in_point"
                            placeholder="00:00:00:00"
                            expanded
                          />
                        </td>
                        <td>
                          <b-input
                            v-model="segment.out_point"
                            placeholder="00:00:00:00"
                            expanded
                          />
                        </td>
                        <td class="has-text-centered">
                          <b-tag type="is-light" size="is-medium">
                            {{ calculateSegmentDuration(segment.in_point, segment.out_point) }}
                          </b-tag>
                        </td>
                        <td>
                          <b-input
                            v-model="segment.notes"
                            placeholder="Notes..."
                            expanded
                          />
                        </td>
                        <td class="has-text-centered">
                          <b-button
                            type="is-success"
                            icon-left="content-save"
                            :loading="savingSegment[`${props.row.id}_${segment.id}`]"
                            @click="updateSegment(segment)"
                          >
                            Save
                          </b-button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>

            <template slot="empty">
              <section class="section">
                <div class="content has-text-grey has-text-centered">
                  <p>
                    <b-icon icon="emoticon-sad" size="is-medium"> </b-icon>
                  </p>
                  <p>No episodes yet.</p>
                </div>
              </section>
            </template>
          </b-table>
        </div>
      </CardComponent>
    </section>
    <b-modal
      :active.sync="showConfirmModal"
      trap-focus
      aria-role="dialog"
      aria-modal
      has-modal-card
      @close="showConfirmModal = false"
    >
      <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
          <p class="modal-card-title">Confirm Delete?</p>
        </header>
        <section class="modal-card-body">
          Are you sure you want to delete this episode?
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end">
          <button
            class="button"
            type="button"
            @click="showConfirmModal = false"
          >
            No
          </button>
          <button class="button is-primary" @click.prevent="deleteEpisode()">
            Yes
          </button>
        </footer>
      </div>
    </b-modal>
    <ChangeManualQCStatusModal
      v-if="showChangeManualQCStatusModal"
      :show="showChangeManualQCStatusModal"
      :media="item"
      :is-episode="true"
      @close-modal="showChangeManualQCStatusModal = false"
    >
    </ChangeManualQCStatusModal>
    <ViewQcResultModal
      v-if="showViewQcResultModal"
      :show="showViewQcResultModal"
      :qc-result-id="item.stitch_qc_result_id"
      @close-modal="showViewQcResultModal = false"
      @refresh="fetchEpisodes"
    >
    </ViewQcResultModal>

    <!-- Unified Media Processing Panel -->
    <section v-if="mediaItem" class="section">
      <MediaProcessingPanel
        :media-id="parseInt($route.params.id)"
        :media-title="mediaItem.title"
        :season-number="mediaItem.mediadata && mediaItem.mediadata.seriesNo"
        @jobs-submitted="fetchEpisodes"
      />
    </section>
  </div>
</template>

<script>
import { sortBy } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import CardComponent from '@/components/CardComponent'
import MediaApi from '@/api/media'
import EpisodeApi from '@/api/episodes'
import BaseDateTimePicker from '@/components/BaseDateTimePicker'
import ChangeManualQCStatusModal from '@/components/ChangeManualQCStatusModal'
import Filters from '@/mixins/filters'
import Utils from '@/mixins/utils'
import ViewQcResultModal from '@/components/ViewQcResultModal'
import MediaProcessingPanel from '@/components/MediaProcessingPanel'
import SegmentsApi from '@/api/segments'
import episodeEventBus, { EPISODE_EVENTS } from '@/services/episodeEventBus'

export default {
  name: 'ManageEpisodes',
  components: {
    CardComponent,
    BaseDateTimePicker,
    ChangeManualQCStatusModal,
    ViewQcResultModal,
    MediaProcessingPanel
  },
  mixins: [Filters, Utils],
  metaInfo: {
    title: 'Manage Episodes'
  },
  computed: {
    ...mapGetters(['user']),
    anySelected() {
      if (this.filteredEpisodes && this.filteredEpisodes.length > 0) {
        return this.filteredEpisodes.find(item => item.selected)
      }
      return false
    },
    selectedCount() {
      if (!this.filteredEpisodes) return 0
      return this.filteredEpisodes.filter(item => item.selected).length
    },
    allFilteredSelected() {
      if (!this.filteredEpisodes || this.filteredEpisodes.length === 0) return false
      return this.filteredEpisodes.every(item => item.selected)
    },
    filteredEpisodes() {
      if (this.search !== '' && this.items.length > 0) {
        let query = this.search.toLowerCase()
        return this.items.filter(
          item =>
            item.title.toLowerCase().includes(query) ||
            item.description.toLowerCase().includes(query)
        )
      } else {
        return this.items
      }
    }
  },
  async created() {
    this.fetchEpisodes()
  },
  mounted() {
    // Listen for episode status changes from EditEpisode
    episodeEventBus.$on(EPISODE_EVENTS.STATUS_CHANGED, this.handleExternalStatusChange)
    episodeEventBus.$on(EPISODE_EVENTS.JOB_SUBMITTED, this.handleExternalJobSubmitted)
    episodeEventBus.$on(EPISODE_EVENTS.FILES_LINKED, this.handleExternalFilesLinked)
    this.startJobPolling()
  },
  beforeDestroy() {
    // Clean up event listeners
    episodeEventBus.$off(EPISODE_EVENTS.STATUS_CHANGED, this.handleExternalStatusChange)
    episodeEventBus.$off(EPISODE_EVENTS.JOB_SUBMITTED, this.handleExternalJobSubmitted)
    episodeEventBus.$off(EPISODE_EVENTS.FILES_LINKED, this.handleExternalFilesLinked)
    this.stopJobPolling()
  },
  data() {
    return {
      mediaItem: null,
      jobProgress: {},
      jobPollTimer: null,
      loading: true,
      regeneratingFilenames: false,
      items: [],
      search: '',
      showConfirmModal: false,
      showChangeManualQCStatusModal: false,
      showViewQcResultModal: false,
      item: {},
      openedDetails: [],
      segmentsByEpisode: {},
      savingSegment: {}
    }
  },
  methods: {
    // Poll live in-flight spot-job progress for this media's episodes.
    async fetchJobProgress() {
      const mediaId = this.$route.params.id
      if (!mediaId) return
      try {
        this.jobProgress = await EpisodeApi.getJobProgressByMedia(mediaId)
      } catch (e) {
        // non-fatal — keep the last known progress
      }
    },
    startJobPolling() {
      this.stopJobPolling()
      this.fetchJobProgress()
      this.jobPollTimer = setInterval(this.fetchJobProgress, 12000)
    },
    stopJobPolling() {
      if (this.jobPollTimer) {
        clearInterval(this.jobPollTimer)
        this.jobPollTimer = null
      }
    },
    ...mapActions(['getUser']),
    onDetailsOpen(row) {
      // Fetch segments when row is expanded (if not already fetched)
      if (!this.segmentsByEpisode[row.id]) {
        this.fetchSegmentsForEpisode(row.id)
      }
    },
    async fetchSegmentsForEpisode(episodeId) {
      try {
        const segments = await SegmentsApi.getAllByEpisodeId(episodeId)
        this.$set(this.segmentsByEpisode, episodeId, segments)
      } catch (error) {
        console.error('Error fetching segments:', error)
        this.$set(this.segmentsByEpisode, episodeId, [])
      }
    },
    async updateSegment(segment) {
      const segmentKey = `${segment.episode_id}_${segment.id}`
      this.$set(this.savingSegment, segmentKey, true)
      try {
        const result = await SegmentsApi.update({
          segmentId: segment.id,
          segmentNumber: segment.segment_number,
          segmentType: segment.segment_type,
          inPoint: segment.in_point,
          outPoint: segment.out_point,
          frameRate: segment.frame_rate,
          notes: segment.notes
        })
        // Backend regen returns scheduledInstancesAffected when duration changed.
        // Surface it so operators see the day-view cascade fired (matches the
        // toast pattern in SegmentsManager.vue / EditEpisode.vue).
        let msg = 'Segment updated!'
        if (result && result.scheduledInstancesAffected > 0) {
          const n = result.scheduledInstancesAffected
          msg = `Segment updated — synced ${n} scheduled instance${n > 1 ? 's' : ''} in day view`
        } else if (result && result.regenerationWarning) {
          msg = 'Segment updated — but day-view sync failed. Day view may be stale.'
        }
        this.$buefy.toast.open({
          message: msg,
          position: 'is-top',
          type: 'is-success',
          duration: 4000
        })
      } catch (error) {
        console.error('Error updating segment:', error)
        this.$buefy.toast.open({
          message: 'Error updating segment',
          position: 'is-top',
          type: 'is-danger'
        })
      } finally {
        this.$set(this.savingSegment, segmentKey, false)
      }
    },
    hasSegments(episodeId) {
      const segments = this.segmentsByEpisode[episodeId]
      return segments && segments.length > 0
    },
    getSegmentCount(episodeId) {
      const segments = this.segmentsByEpisode[episodeId]
      return segments ? segments.length : '...'
    },
    formatTimecode(timecode) {
      return timecode || '00:00:00:00'
    },
    calculateSegmentDuration(inPoint, outPoint) {
      if (!inPoint || !outPoint) return '--:--:--'
      try {
        // Parse timecode format HH:MM:SS:FF
        const parseTimecode = (tc) => {
          const parts = tc.split(':')
          if (parts.length !== 4) return 0
          const [h, m, s, f] = parts.map(p => parseInt(p, 10))
          // Assuming 25fps for frame calculation
          return (h * 3600 + m * 60 + s) * 25 + f
        }
        const inFrames = parseTimecode(inPoint)
        const outFrames = parseTimecode(outPoint)
        const durationFrames = outFrames - inFrames
        if (durationFrames < 0) return '--:--:--'

        const totalSeconds = Math.floor(durationFrames / 25)
        const frames = durationFrames % 25
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60

        const pad = (n) => String(n).padStart(2, '0')
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}:${pad(frames)}`
      } catch (e) {
        return '--:--:--'
      }
    },
    getTxFileStatusType(episode) {
      // Primary question: Is there a TX file assigned?
      const hasTxFile = episode.storage_path || episode.stitched_file_path
      const stitchStatus = episode.stitch_status
      const stitchQcStatus = episode.stitch_qc_status
      const txIngestStatus = episode.tx_ingest_status
      const transcodeStatus = episode.transcode_status
      const qcStatus = episode.qc_status
      const txWorkflowStatus = episode.tx_workflow_status

      // HIGHEST PRIORITY: Check tx_workflow_status for version changes
      if (txWorkflowStatus === 'version_changed') {
        return 'is-danger' // File changed since approval - CRITICAL
      }

      // 1. NO TX FILE - check if pipeline is in progress
      if (!hasTxFile) {
        if (stitchStatus === 'processing' || stitchStatus === 'queued' || stitchStatus === 'pending') {
          return 'is-warning' // Processing
        }
        if (stitchStatus === 'failed') {
          return 'is-danger' // Stitch Failed
        }
        return 'is-light' // No TX File
      }

      // Check tx_workflow_status for staged/approved states
      if (txWorkflowStatus === 'staged') return 'is-success' // Staged to Cinegy
      if (txWorkflowStatus === 'approved') return 'is-success' // Approved for playout

      // 2. TX FILE EXISTS - check TX Ingest status first (direct upload workflow)
      if (txIngestStatus === 'queued' || txIngestStatus === 'processing') return 'is-warning' // TX Ingest Running
      if (txIngestStatus === 'completed') return 'is-success' // TX Ingest Complete
      if (txIngestStatus === 'failed') return 'is-danger' // TX Ingest Failed

      // 2a. Check transcode status (batch transcode workflow for single-file format conversion)
      if (transcodeStatus === 'queued' || transcodeStatus === 'processing') return 'is-warning' // Transcoding
      if (transcodeStatus === 'completed') return 'is-success' // Transcode Complete
      if (transcodeStatus === 'failed') return 'is-danger' // Transcode Failed

      // 2b. Check standalone QC status (QC workflow for existing TX files)
      if (qcStatus === 'queued' || qcStatus === 'processing') return 'is-warning' // QC Running
      if (qcStatus === 'passed') return 'is-success' // QC Passed
      if (qcStatus === 'passed_with_warnings') return 'is-success' // QC Passed*
      if (qcStatus === 'overridden') return 'is-info' // QC Overridden
      if (qcStatus === 'failed') return 'is-danger' // QC Failed

      // 2c. Check if RE-PROCESSING (new stitch job submitted for existing TX file)
      if (stitchStatus === 'queued' || stitchStatus === 'processing' || stitchStatus === 'pending') {
        return 'is-warning' // Re-processing
      }

      // 3. TX FILE EXISTS - check stitch QC status (stitch workflow)
      if (stitchQcStatus === 'passed') return 'is-success' // TX Ready
      if (stitchQcStatus === 'passed_with_warnings') return 'is-success' // TX Ready*
      if (stitchQcStatus === 'overridden') return 'is-info' // QC Overridden
      if (stitchQcStatus === 'failed') return 'is-danger' // QC Failed
      if (stitchQcStatus === 'pending' || stitchQcStatus === 'processing') return 'is-warning' // QC Pending

      // TX file exists but no QC run (direct upload/assignment)
      return 'is-info' // Assigned (No QC)
    },
    getTxFileStatusLabel(episode) {
      // Primary question: Is there a TX file assigned?
      const hasTxFile = episode.storage_path || episode.stitched_file_path
      const stitchStatus = episode.stitch_status
      const stitchQcStatus = episode.stitch_qc_status
      const txIngestStatus = episode.tx_ingest_status
      const transcodeStatus = episode.transcode_status
      const qcStatus = episode.qc_status
      const txWorkflowStatus = episode.tx_workflow_status
      const needsSoftSegmentation = episode.needs_soft_segmentation === true || episode.needs_soft_segmentation === 1

      // Soft-segmentation indicator suffix (added to final status)
      const segIndicator = needsSoftSegmentation && hasTxFile ? ' (seg)' : ''

      // HIGHEST PRIORITY: Check for version change
      if (txWorkflowStatus === 'version_changed') {
        return 'Version Changed!'
      }

      // Check tx_workflow_status for staged/approved states
      if (txWorkflowStatus === 'staged') return 'Staged' + segIndicator
      if (txWorkflowStatus === 'approved') return 'Approved' + segIndicator

      // 1. NO TX FILE - check if pipeline is in progress
      if (!hasTxFile) {
        if (stitchStatus === 'processing') return 'Processing...'
        if (stitchStatus === 'queued') return 'Queued'
        if (stitchStatus === 'pending') return 'Pending'
        if (stitchStatus === 'failed') return 'Stitch Failed'
        return 'No TX File'
      }

      // 2. TX FILE EXISTS - check TX Ingest status first (direct upload workflow)
      if (txIngestStatus === 'queued') return 'QC Queued'
      if (txIngestStatus === 'processing') return 'QC Running...'
      if (txIngestStatus === 'completed') return 'TX Ready' + segIndicator
      if (txIngestStatus === 'failed') return 'TX Ingest Failed'

      // 2a. Check transcode status (batch transcode workflow for single-file format conversion)
      if (transcodeStatus === 'queued') return 'Transcode Queued'
      if (transcodeStatus === 'processing') return 'Transcoding...'
      if (transcodeStatus === 'completed') return 'Transcoded' + segIndicator
      if (transcodeStatus === 'failed') return 'Transcode Failed'

      // 2b. Check standalone QC status (QC workflow for existing TX files)
      if (qcStatus === 'queued') return 'QC Queued'
      if (qcStatus === 'processing') return 'QC Running...'
      if (qcStatus === 'passed') return 'QC Passed' + segIndicator
      if (qcStatus === 'passed_with_warnings') return 'QC Passed*' + segIndicator
      if (qcStatus === 'overridden') return 'QC Overridden' + segIndicator
      if (qcStatus === 'failed') return 'QC Failed'

      // 2c. Check if RE-PROCESSING (new stitch job submitted for existing TX file)
      if (stitchStatus === 'processing') return 'Re-processing...'
      if (stitchStatus === 'queued') return 'Re-queued'
      if (stitchStatus === 'pending') return 'Re-pending'

      // 3. TX FILE EXISTS - check stitch QC status (stitch workflow)
      if (stitchQcStatus === 'passed') return 'TX Ready' + segIndicator
      if (stitchQcStatus === 'passed_with_warnings') return 'TX Ready*' + segIndicator
      if (stitchQcStatus === 'overridden') return 'QC Overridden' + segIndicator
      if (stitchQcStatus === 'failed') return 'QC Failed'
      if (stitchQcStatus === 'processing') return 'QC Running...'
      if (stitchQcStatus === 'pending') return 'QC Pending'

      // TX file exists but no QC run (direct upload/assignment)
      return 'Assigned' + segIndicator
    },
    backToTitle() {
      this.$router.push({
        name: 'EditMedia',
        params: { id: this.$route.params.id }
      }).catch(err => {
        // Ignore NavigationDuplicated errors
        if (err.name !== 'NavigationDuplicated') {
          throw err
        }
      })
    },
    showQcJobResult(item) {
      this.item = item
      this.showViewQcResultModal = true
    },
    changeManualStatus(item) {
      this.item = item
      this.showChangeManualQCStatusModal = true
    },
    updateRow(item) {
      item.episodeId = item.id
      item.episodeNumber = item.episode_number
      // MERGE episodedata with existing — never REPLACE.
      // The backend writes keys this UI doesn't surface (tx_linked_at, last_job_uuid,
      // tx_duration_ms) after stitch/transcode. Rebuilding from scratch wipes them
      // and breaks ETag/version detection + retention required-files matching. Each
      // surface field only overrides when explicitly defined on the row.
      // Incident: ep 4026 (2026-06-09 08:05 qc2+siyaya inline edit) wiped
      // episodedata {...}→{}, flipped needs_soft_segmentation, retention deleted the
      // TX file, Cinegy went to colour bars at the 15:00 cue.
      const existingEd = (item.episodedata && typeof item.episodedata === 'object' && !Array.isArray(item.episodedata)) ? item.episodedata : {}
      const mergedEd = { ...existingEd }
      if (item.releaseDate !== undefined) mergedEd.releaseDate = item.releaseDate
      if (item.episodeLength !== undefined) mergedEd.episodeLength = item.episodeLength
      if (item.vodStartDate !== undefined) mergedEd.vodStartDate = item.vodStartDate
      if (item.vodExpiryDate !== undefined) mergedEd.vodExpiryDate = item.vodExpiryDate
      if (item.ageRestriction !== undefined) mergedEd.ageRestriction = item.ageRestriction
      item.episodedata = mergedEd
      // Description: don't coerce undefined→'' (was wiping description silently).
      // Only include if explicitly defined on the row; backend keeps existing
      // value when key is omitted from the payload.
      if (item.description === undefined) {
        delete item.description
      }
      // Dedicated VOD datetime columns
      item.vod_start_datetime = item.vodStartDate || null
      item.vod_expiry_datetime = item.vodExpiryDate || null
      EpisodeApi.update(item)
        .then(async () => {
          // Auto-regenerate future-scheduled segments (no Skip option — stale exports are
          // worse than the schedule shift). Backend scopes to future-only instances, so
          // past airings are untouched. Toast only fires when something was actually changed.
          try {
            const regenResult = await EpisodeApi.regenerateScheduleSegments(item.id)
            if (regenResult && regenResult.scheduledInstancesAffected > 0) {
              this.$buefy.toast.open({
                message: `Updated ${regenResult.scheduledInstancesAffected} scheduled instance${regenResult.scheduledInstancesAffected > 1 ? 's' : ''} in day view`,
                position: 'is-top',
                type: 'is-success',
                duration: 4000
              })
            }
          } catch (regenErr) {
            console.error('Failed to regenerate schedule segments:', regenErr)
            this.$buefy.toast.open({
              message: 'Episode saved but schedule regeneration failed — day view and exports may be stale.',
              position: 'is-top',
              type: 'is-warning',
              duration: 6000
            })
          }
        })
        .catch(err => {
          console.log(err)
          this.$buefy.toast.open({
            message: 'Error updating episode',
            position: 'is-top',
            type: 'is-danger'
          })
          this.loading = false
        })
    },
    toggleSelectAll() {
      // Select all currently-filtered rows, or clear if all already selected.
      // Uses $set so Vue picks up the mutation on rows that may not have had
      // `selected` defined yet.
      const target = !this.allFilteredSelected
      for (const row of this.filteredEpisodes) {
        this.$set(row, 'selected', target)
      }
    },
    deleteEpisodes() {
      let items = this.filteredEpisodes.filter(item => item.selected)
      this.$buefy.dialog.confirm({
        message: `Are you sure you want to delete the ${items.length} episodes?`,
        onConfirm: async () => {
          this.loading = true
          await EpisodeApi.removeMultiple(items.map(item => item.id))
          this.$buefy.toast.open({
            message: 'Episodes removed successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          this.loading = false
          this.items = this.items.filter(item => !item.selected)
        }
      })
    },
    confirmDelete(item) {
      this.showConfirmModal = true
      this.item = item
    },
    deleteEpisode() {
      EpisodeApi.remove({
        episodeId: this.item.id
      })
        .then(() => {
          this.$buefy.toast.open({
            message: 'Episode deleted successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          this.fetchEpisodes()
          this.showConfirmModal = false
          this.loading = false
        })
        .catch(err => {
          console.log(err)
          this.$buefy.toast.open({
            message: 'Error deleting episode',
            position: 'is-top',
            type: 'is-danger'
          })
          this.loading = false
        })
    },
    async fetchEpisodes() {
      this.loading = true
      const mediaId = this.$route.params.id
      if (!mediaId) {
        console.error('fetchEpisodes: No media ID in route params')
        this.$buefy.toast.open({
          message: 'Error: Media ID not found in URL',
          position: 'is-top',
          type: 'is-danger',
          duration: 5000
        })
        this.loading = false
        return
      }
      try {
        this.mediaItem = await MediaApi.getById(mediaId)
      } catch (error) {
        console.error('fetchEpisodes: Failed to load media', error)
        this.$buefy.toast.open({
          message: 'Failed to load media data: ' + (error?.message || 'Unknown error'),
          position: 'is-top',
          type: 'is-danger',
          duration: 5000
        })
        this.loading = false
        return
      }
      if (this.mediaItem.mediadata && this.mediaItem.mediadata !== '') {
        this.mediaItem.mediadata = JSON.parse(this.mediaItem.mediadata)
      }
      if (this.mediaItem.metadata && this.mediaItem.metadata !== '') {
        this.mediaItem.metadata = JSON.parse(this.mediaItem.metadata)
      }

      EpisodeApi.getAllByMediaId(this.$route.params.id).then(items => {
        items.forEach(item => {
          item.episodeId = item.id
          item.episode_number = parseInt(item.episode_number, 10)
          if (item.episodedata && item.episodedata !== '') {
            item.episodedata = JSON.parse(item.episodedata)
            if (item.episodedata.episodeLength) {
              item.episodeLength = item.episodedata.episodeLength
            }
            if (item.episodedata.ageRestriction) {
              item.ageRestriction = item.episodedata.ageRestriction
            }
            // VOD dates from episodedata (legacy fallback)
            if (item.episodedata.vodStartDate && !item.vod_start_datetime) {
              item.vodStartDate = new Date(item.episodedata.vodStartDate)
            }
            if (item.episodedata.vodExpiryDate && !item.vod_expiry_datetime) {
              item.vodExpiryDate = new Date(item.episodedata.vodExpiryDate)
            }
          }
          // Initialize defaults for inline editing
          if (!item.ageRestriction) {
            item.ageRestriction = null
          }
          if (!item.description) {
            item.description = ''
          }

          // VOD dates from auto-calculated columns (priority over episodedata)
          if (item.vod_start_datetime) {
            item.vodStartDate = new Date(item.vod_start_datetime)
          }
          if (item.vod_expiry_datetime) {
            item.vodExpiryDate = new Date(item.vod_expiry_datetime)
          }

          if (this.mediaItem) {
            // Use tx_filename from database if available, otherwise generate display name
            item.fileName = item.tx_filename || this.generateFileName(this.mediaItem, item)
            // QC status comes from episode's stitch_qc_status field
            item.qcstatus = item.stitch_qc_status || null
          }
        })
        this.items = items.sort((a, b) => parseInt(a.episode_number) - parseInt(b.episode_number))
        this.loading = false

        // Fetch segment counts for all episodes in parallel
        this.fetchAllSegmentCounts()
      })
    },
    async fetchAllSegmentCounts() {
      // Fetch segments for all episodes in parallel to show counts immediately
      const promises = this.items.map(item =>
        SegmentsApi.getAllByEpisodeId(item.id)
          .then(segments => {
            this.$set(this.segmentsByEpisode, item.id, segments)
          })
          .catch(() => {
            this.$set(this.segmentsByEpisode, item.id, [])
          })
      )
      await Promise.all(promises)
    },
    async autoGenerate() {
      const mediaId = this.$route.params.id
      if (!mediaId) {
        this.$buefy.toast.open({
          message: 'Error: Media ID not found. Please navigate to this page from the media list.',
          position: 'is-top',
          type: 'is-danger',
          duration: 5000
        })
        return
      }
      this.loading = true
      try {
        await EpisodeApi.autoGenerate(mediaId)
        await this.fetchEpisodes()
        this.$buefy.toast.open({
          message: 'Episodes auto-generated successfully!',
          position: 'is-top',
          type: 'is-success'
        })
      } catch (error) {
        console.error('Auto-generate error:', error)
        const errorMessage = error?.data?.message || error?.message || 'Failed to auto-generate episodes'
        this.$buefy.toast.open({
          message: errorMessage,
          position: 'is-top',
          type: 'is-danger',
          duration: 5000
        })
      } finally {
        this.loading = false
      }
    },
    async regenerateTxFilenames() {
      this.regeneratingFilenames = true
      try {
        const result = await EpisodeApi.regenerateTxFilenames(
          this.$route.params.id
        )
        await this.fetchEpisodes()
        this.$buefy.toast.open({
          message: result.message || 'TX filenames regenerated successfully!',
          position: 'is-top',
          type: 'is-success',
          duration: 5000
        })
      } catch (error) {
        const errorMessage =
          error?.data?.message ||
          error?.message ||
          'Failed to regenerate TX filenames'
        this.$buefy.toast.open({
          message: errorMessage,
          position: 'is-top',
          type: 'is-danger',
          duration: 5000
        })
      } finally {
        this.regeneratingFilenames = false
      }
    },
    async copyText(mytext) {
      try {
        await navigator.clipboard.writeText(mytext)
        this.$buefy.toast.open({
          message: 'Copied successfully!',
          position: 'is-top',
          type: 'is-success'
        })
      } catch ($e) {
        alert('Cannot copy')
      }
    },
    async copyAll() {
      try {
        const list = this.items.map(item => item.fileName).join('\r\n')
        await navigator.clipboard.writeText(list)
        this.$buefy.toast.open({
          message: 'Copied successfully!',
          position: 'is-top',
          type: 'is-success'
        })
      } catch ($e) {
        alert('Cannot copy')
      }
    },
    async exportSegmentsCSV() {
      try {
        const mediaId = this.$route.params.id
        const blob = await SegmentsApi.exportMediaCSV(mediaId)
        const filename = `media_${mediaId}_all_segments_${Date.now()}.csv`
        SegmentsApi.downloadCSV(blob, filename)
        this.$buefy.toast.open({
          message: 'Segments CSV exported successfully!',
          position: 'is-top',
          type: 'is-success'
        })
      } catch (error) {
        this.$buefy.toast.open({
          message:
            'Failed to export segments CSV. Make sure episodes have segments defined.',
          position: 'is-top',
          type: 'is-danger',
          duration: 5000
        })
      }
    },

    /**
     * Handle external status change event from EditEpisode
     */
    handleExternalStatusChange(data) {
      // Only process if it's for this media
      const mediaId = this.$route.params.id
      if (data.mediaId && data.mediaId !== parseInt(mediaId)) return

      console.log('ManageEpisodes: External status change received:', data)

      // Find and update the episode in our list
      const episode = this.items.find(ep => ep.id === data.episodeId)
      if (episode) {
        if (data.stitchStatus) episode.stitch_status = data.stitchStatus
        if (data.stitchQcStatus) episode.stitch_qc_status = data.stitchQcStatus
        if (data.transcodeStatus) episode.transcode_status = data.transcodeStatus
        if (data.qcStatus) episode.qc_status = data.qcStatus
      }
    },

    /**
     * Handle external job submitted event from EditEpisode
     */
    handleExternalJobSubmitted(data) {
      // Only process if it's for this media
      const mediaId = this.$route.params.id
      if (data.mediaId && data.mediaId !== parseInt(mediaId)) return

      console.log('ManageEpisodes: External job submitted:', data)

      // Find and update the episode in our list to show queued status
      const episode = this.items.find(ep => ep.id === data.episodeId)
      if (episode) {
        if (data.jobType === 'stitch') {
          episode.stitch_status = 'queued'
        } else if (data.jobType === 'transcode') {
          episode.transcode_status = 'queued'
        } else if (data.jobType === 'qc') {
          episode.qc_status = 'queued'
        }
      }
    },

    /**
     * Handle external files linked event from EditEpisode
     */
    handleExternalFilesLinked(data) {
      // Only process if it's for this media
      const mediaId = this.$route.params.id
      if (data.mediaId && data.mediaId !== parseInt(mediaId)) return

      console.log('ManageEpisodes: External files linked:', data)

      // Refresh episode data to reflect linked files
      this.fetchEpisodes()
    },

    /**
     * Emit job submitted event to notify EditEpisode
     * Call this when jobs are submitted from ManageEpisodes (via MediaProcessingPanel)
     */
    emitJobSubmitted(jobData) {
      episodeEventBus.$emit(EPISODE_EVENTS.JOB_SUBMITTED, {
        episodeId: jobData.episodeId,
        mediaId: parseInt(this.$route.params.id),
        jobType: jobData.jobType || 'stitch',
        status: 'queued'
      })
    },

    /**
     * Emit status changed event to notify EditEpisode
     * Call this when status changes from ManageEpisodes
     */
    emitStatusChanged(episodeId, statusData) {
      episodeEventBus.$emit(EPISODE_EVENTS.STATUS_CHANGED, {
        episodeId,
        mediaId: parseInt(this.$route.params.id),
        ...statusData
      })
    }
  }
}
</script>

<style scoped>
.filename-text {
  font-size: 0.7em;
  font-family: 'Courier New', monospace;
  word-break: break-all;
  line-height: 1.3;
  margin-bottom: 2px;
}

.segment-details-container {
  background-color: #f5f7fa;
  border-radius: 6px;
  padding: 20px;
  margin: 0;
}

.segment-table {
  background-color: white;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.segment-table thead th {
  background-color: #363636;
  color: white;
  font-size: 0.9em;
  padding: 12px;
  text-align: center;
}

.segment-table tbody td {
  padding: 10px 12px;
  vertical-align: middle;
}

.segment-table tbody tr:hover {
  background-color: #f0f7ff;
}

.segment-table .input {
  font-family: 'Courier New', monospace;
  font-size: 1em;
}
</style>

<style>
/* Override Buefy detail row styling - not scoped so it applies to nested content */
.b-table .detail {
  background-color: transparent !important;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.b-table .detail td {
  padding: 0 !important;
}
</style>
